import React from 'react';

import GenericAttachedItemsSidebar from 'app/components/general/GenericAttachedItemsSidebar';
import NoResultsMessage from 'app/components/general/NoResultsMessage';
import {useCurrentUserState} from 'app/current-user/api';
import {getCurrentUserPerms} from 'app/current-user/utils';
import {keepUpdatedEnhancer} from 'app/general/keepUpdatedEnhancer';
import {searchPolicies} from 'app/policies/actions';
import {
  useAttachPolicyToProcedure,
  useAttachPolicyToProcess,
  useDetachPolicyFromProcedure,
  useDetachPolicyFromProcess,
} from 'app/policies/api';
import {runWhenAllRelatedMatch} from 'app/subscriptions';
import {POLICIES_NEW_POLICY} from 'app/urls';
import {SIDEBAR_PAGE_SIZE} from 'app/utils/constants';
import {getContentType} from 'app/utils/content-type';
import {getPolicyName} from 'app/utils/fallbacks';
import {gt} from 'app/utils/i18n';
import {get} from 'app/utils/request';

import type {RelatedPolicyType} from 'app/policies/types';
import type {
  ModelChangeMessageType,
  ModelSubscribedComponentPropsType,
} from 'app/subscriptions';

type Props = {
  items: Array<RelatedPolicyType>;
  onClose: () => void;
  objectId: string | number;
  objectURL: string;
  canEdit: boolean;
  contentType: 'procedure' | 'proceduregroup';
  showFilterField: boolean;
  loadItems: () => Promise<any>;
};
export const DetailPageGenericPoliciesSidebar = keepUpdatedEnhancer<Props>(
  ({
    objectURL,
    objectId,
    loadItems,
    canEdit,
    showFilterField,
    contentType,
    ...other
  }: Props & ModelSubscribedComponentPropsType) => {
    const {isTeamMember} = getCurrentUserPerms(useCurrentUserState().data!);
    const [attachPolicyToProcess] = useAttachPolicyToProcess();
    const [attachPolicyToProcedure] = useAttachPolicyToProcedure();
    const [detachPolicyFromProcess] = useDetachPolicyFromProcess();
    const [detachPolicyFromProcedure] = useDetachPolicyFromProcedure();

    const ct = getContentType(contentType);
    const props = {
      //  show/edit items
      loadItems,
      title: 'Policies',
      itemNameProcessor: getPolicyName,
      handleModelChange: (message: ModelChangeMessageType) => {
        runWhenAllRelatedMatch(
          message,
          'policy',
          {
            content_type: contentType,
            id: typeof objectId === 'string' ? parseInt(objectId) : objectId,
          },
          () => loadItems()
        );
      },
      onRemove: (item) => {
        return contentType === 'proceduregroup'
          ? detachPolicyFromProcess(item.link_url)
          : detachPolicyFromProcedure(item.link_url);
      },
      itemComponentProps: (policy, attached) => {
        return {
          disableAndShowTooltipText: !canEdit
            ? gt(
                'You have to be a %s manager to add or remove policies',
                ct.name
              )
            : null,
          actionTooltip: attached
            ? gt('Remove this policy from %s', ct.name)
            : gt('Add this policy to %s', ct.name),
        };
      },
      // attach
      showFilterField,
      searchPlaceholder: gt('Filter policies...'),
      onSearch: (search) => searchPolicies(search, SIDEBAR_PAGE_SIZE),
      onLoadMore: (response) => get(response.next),
      onAttach: (item) =>
        contentType === 'proceduregroup'
          ? attachPolicyToProcess({
              genericLinks: [
                {
                  policy: item.url,
                  process: objectURL,
                },
              ],
            })
          : attachPolicyToProcedure({
              genericLinks: [
                {
                  policy: item.url,
                  procedure: objectURL,
                },
              ],
            }),
      // create
      canCreate: isTeamMember,
      createLabel: gt('Create Policy'),
      createURL: POLICIES_NEW_POLICY,
      emptyResultsComponents: (
        <NoResultsMessage
          onSidebar={true}
          icon="fa-umbrella"
          head={gt('No policies')}
        >
          {showFilterField
            ? gt(
                'You can attach and detach policies from this %s or create a new policy using the button below',
                ct.name
              )
            : null}
        </NoResultsMessage>
      ),
      ...other,
    };
    return <GenericAttachedItemsSidebar {...props} />;
  }
);
